import { useMemo } from 'react';
import type { CommissionPlanWithDetails, CommissionTier } from './useCommissionPlans';

export interface CommissionCalculationInput {
  plan: CommissionPlanWithDetails | null;
  totalSales: number; // total de contratos vendidos
  monthlyGoal: number; // meta mensal
  totalCashIn: number; // total recebido (cash-in)
  consultantTenureDays?: number; // dias desde contratação
  activeAcceleratorKeys?: string[]; // aceleradores ativos
}

export interface CommissionCalculationResult {
  achievementPct: number;
  tier: CommissionTier | null;
  baseCommissionPct: number;
  rampupBonusPct: number;
  acceleratorsPct: number;
  totalCommissionPct: number;
  commissionAmount: number;
  planName: string;
  isRampUp: boolean;
}

/**
 * Find the matching tier for a given achievement percentage
 */
function findTier(tiers: CommissionTier[], achievementPct: number): CommissionTier | null {
  if (achievementPct < (tiers[0]?.min_goal_pct ?? 60)) return null;

  for (const tier of tiers) {
    const max = tier.max_goal_pct ?? Infinity;
    if (achievementPct >= tier.min_goal_pct && achievementPct <= max) {
      return tier;
    }
  }
  return null;
}

/**
 * Calculate ramp-up bonus based on plan config and tenure
 */
function calculateRampUp(
  plan: CommissionPlanWithDetails,
  tenureDays: number,
  basePct: number
): { isRampUp: boolean; bonusPct: number } {
  if (!plan.rampup_type || tenureDays >= plan.rampup_duration_days) {
    return { isRampUp: false, bonusPct: 0 };
  }

  if (plan.rampup_type === 'tier_doubled') {
    // Tier doubled: the base commission is doubled
    return { isRampUp: true, bonusPct: basePct }; // doubles it
  }

  if (plan.rampup_type === 'bonus_pct') {
    const config = plan.rampup_config || {};
    const monthNumber = Math.floor(tenureDays / 30) + 1;
    const key = `month_${monthNumber}`;
    const bonus = config[key] ?? 0;
    return { isRampUp: true, bonusPct: bonus };
  }

  return { isRampUp: false, bonusPct: 0 };
}

/**
 * Pure calculation function (no hooks) for use in other contexts
 */
export function calculateCommission(input: CommissionCalculationInput): CommissionCalculationResult {
  const { plan, totalSales, monthlyGoal, totalCashIn, consultantTenureDays = 999, activeAcceleratorKeys = [] } = input;

  if (!plan || monthlyGoal <= 0) {
    return {
      achievementPct: 0,
      tier: null,
      baseCommissionPct: 0,
      rampupBonusPct: 0,
      acceleratorsPct: 0,
      totalCommissionPct: 0,
      commissionAmount: 0,
      planName: plan?.name ?? '',
      isRampUp: false,
    };
  }

  const achievementPct = (totalSales / monthlyGoal) * 100;
  const tiers = plan.commission_tiers || [];
  const tier = findTier(tiers, achievementPct);
  const baseCommissionPct = tier?.commission_pct ?? 0;

  const { isRampUp, bonusPct: rampupBonusPct } = tier
    ? calculateRampUp(plan, consultantTenureDays, baseCommissionPct)
    : { isRampUp: false, bonusPct: 0 };

  // Sum eligible accelerators
  const accelerators = plan.commission_accelerators || [];
  const acceleratorsPct = accelerators
    .filter(a => activeAcceleratorKeys.includes(a.key))
    .reduce((sum, a) => sum + a.bonus_pct, 0);

  // Cap accelerators at max
  const cappedAcceleratorsPct = Math.min(acceleratorsPct, plan.max_accelerator_pct);

  const totalCommissionPct = baseCommissionPct + rampupBonusPct + cappedAcceleratorsPct;
  const commissionAmount = totalCashIn * (totalCommissionPct / 100);

  return {
    achievementPct,
    tier,
    baseCommissionPct,
    rampupBonusPct,
    acceleratorsPct: cappedAcceleratorsPct,
    totalCommissionPct,
    commissionAmount,
    planName: plan.name,
    isRampUp,
  };
}

/**
 * React hook wrapper for commission calculation
 */
export function useCommissionCalculation(input: CommissionCalculationInput): CommissionCalculationResult {
  return useMemo(() => calculateCommission(input), [
    input.plan?.id,
    input.totalSales,
    input.monthlyGoal,
    input.totalCashIn,
    input.consultantTenureDays,
    JSON.stringify(input.activeAcceleratorKeys),
  ]);
}
